import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  User, 
  Phone, 
  Smartphone, 
  Calendar,
  DollarSign,
  Loader2,
  Check,
  UserCheck
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { MemberData } from './types';
import { PLAN_PRICING } from './types';
import { format } from 'date-fns';

interface ActivationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  member: MemberData;
  adminUserId: string;
  onActivated: () => void;
}

interface SellerOption {
  user_id: string;
  nombre: string;
  ubicacion_tienda: string | null;
}

const planLabels: Record<string, string> = {
  bronce: 'Bronce',
  plata: 'Plata',
  oro: 'Oro',
};

const periodLabels: Record<string, string> = {
  mensual: 'Mensual',
  anual: 'Anual',
};

const planColors: Record<string, string> = {
  bronce: 'text-amber-600',
  plata: 'text-slate-400',
  oro: 'text-yellow-500',
};

const planPoints: Record<string, number> = {
  bronce: 10,
  plata: 20,
  oro: 40,
};

export const ActivationDialog: React.FC<ActivationDialogProps> = ({
  isOpen,
  onClose,
  member,
  adminUserId,
  onActivated,
}) => {
  const { toast } = useToast();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [selectedSellerId, setSelectedSellerId] = useState<string>('');
  const [loadingSellers, setLoadingSellers] = useState(true);

  // Fetch sellers when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchSellers();
    }
  }, [isOpen]);

  const fetchSellers = async () => {
    setLoadingSellers(true);
    try {
      // Get ranking stats which includes all sellers
      const { data: rankingData } = await (supabase as any).rpc('get_seller_ranking_stats');
      
      if (rankingData && rankingData.length > 0) {
        const sellerIds = rankingData.map((r: any) => r.seller_id);
        
        // Get seller profiles
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, nombre, ubicacion_tienda')
          .in('user_id', sellerIds);

        setSellers(profiles || []);
      } else {
        // Fallback: try direct query for sellers
        const { data: sellerRoles } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'vendedor');

        if (sellerRoles && sellerRoles.length > 0) {
          const sellerIds = sellerRoles.map(r => r.user_id);
          
          const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id, nombre, ubicacion_tienda')
            .in('user_id', sellerIds);

          setSellers(profiles || []);
        }
      }
    } catch (error) {
      console.error('Error fetching sellers:', error);
    } finally {
      setLoadingSellers(false);
    }
  };

  if (!member.membership) return null;

  const { plan, billing_period } = member.membership;
  const amount = PLAN_PRICING[plan][billing_period];

  const calculateEndDate = () => {
    const today = new Date();
    if (billing_period === 'mensual') {
      today.setMonth(today.getMonth() + 1);
    } else {
      today.setFullYear(today.getFullYear() + 1);
    }
    return today.toISOString().split('T')[0];
  };

  const updateSellerCycleStats = async (sellerId: string, membershipPlan: string) => {
    const currentCycle = format(new Date(), 'yyyy-MM');
    
    try {
      // Try to get existing cycle stats
      const { data: existingStats } = await supabase
        .from('seller_cycle_stats')
        .select('*')
        .eq('seller_id', sellerId)
        .eq('cycle_month_year', currentCycle)
        .maybeSingle();

      const today = new Date().toISOString().split('T')[0];
      
      if (existingStats) {
        // Update existing stats
        const updates: any = {
          last_sale_date: today,
          consecutive_sales_count: existingStats.consecutive_sales_count + 1,
        };
        
        // Update plan-specific counter
        if (membershipPlan === 'bronce') {
          updates.bronce_sales = existingStats.bronce_sales + 1;
        } else if (membershipPlan === 'plata') {
          updates.plata_sales = existingStats.plata_sales + 1;
        } else if (membershipPlan === 'oro') {
          updates.oro_sales = existingStats.oro_sales + 1;
        }

        // Check for first_sale milestone
        if (!existingStats.first_sale_achieved) {
          updates.first_sale_achieved = true;
        }

        // Check consecutive days
        if (existingStats.last_sale_date) {
          const lastDate = new Date(existingStats.last_sale_date);
          const todayDate = new Date(today);
          const diffDays = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffDays === 1) {
            updates.consecutive_days_count = existingStats.consecutive_days_count + 1;
            // Check for consistency milestone (5 consecutive days)
            if (updates.consecutive_days_count >= 5 && !existingStats.consistency_achieved) {
              updates.consistency_achieved = true;
            }
          } else if (diffDays > 1) {
            updates.consecutive_days_count = 1;
          }
        } else {
          updates.consecutive_days_count = 1;
        }

        await supabase
          .from('seller_cycle_stats')
          .update(updates)
          .eq('id', existingStats.id);

      } else {
        // Create new cycle stats
        const newStats: any = {
          seller_id: sellerId,
          cycle_month_year: currentCycle,
          first_sale_achieved: true,
          last_sale_date: today,
          consecutive_sales_count: 1,
          consecutive_days_count: 1,
          bronce_sales: membershipPlan === 'bronce' ? 1 : 0,
          plata_sales: membershipPlan === 'plata' ? 1 : 0,
          oro_sales: membershipPlan === 'oro' ? 1 : 0,
        };

        await supabase
          .from('seller_cycle_stats')
          .insert(newStats);
      }
    } catch (error) {
      console.error('Error updating seller cycle stats:', error);
    }
  };

  const handleActivate = async () => {
    if (!member.membership) return;
    
    // Validate seller selection
    if (!selectedSellerId) {
      toast({
        title: 'Selecciona un vendedor',
        description: 'Debes indicar quién realizó la venta para asignar los puntos correctamente.',
        variant: 'destructive',
      });
      return;
    }
    
    setSubmitting(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const endDate = calculateEndDate();

      // 1. Update membership to active with sold_by
      const { error: membershipError } = await supabase
        .from('memberships')
        .update({
          is_active: true,
          activated_at: new Date().toISOString(),
          activated_by: adminUserId,
          sold_by: selectedSellerId,
          start_date: today,
          end_date: endDate,
        })
        .eq('id', member.membership.id);

      if (membershipError) throw membershipError;

      // 2. Update seller cycle stats for points
      await updateSellerCycleStats(selectedSellerId, plan);

      // 3. Insert payment record
      const { error: paymentError } = await supabase
        .from('membership_payments')
        .insert({
          membership_id: member.membership.id,
          user_id: member.profile.user_id,
          amount: amount,
          plan: plan,
          billing_period: billing_period,
          payment_type: 'activation',
          processed_by: adminUserId,
          notes: notes.trim() || null,
        });

      if (paymentError) throw paymentError;

      // 4. Get seller name for audit log
      const selectedSeller = sellers.find(s => s.user_id === selectedSellerId);

      // 5. Log admin action with seller info
      await supabase
        .from('admin_audit_logs')
        .insert({
          admin_user_id: adminUserId,
          action_type: 'membership_activation',
          target_user_id: member.profile.user_id,
          details: {
            amount: amount,
            plan: plan,
            billing_period: billing_period,
            payment_type: 'activation',
            notes: notes.trim() || null,
            sold_by: selectedSellerId,
            sold_by_name: selectedSeller?.nombre || 'Desconocido',
            points_awarded: planPoints[plan],
          },
        });

      toast({
        title: '¡Membresía activada!',
        description: `Se activó la membresía de ${member.profile.nombre}. ${selectedSeller?.nombre} ganó +${planPoints[plan]} pts.`,
      });

      onActivated();
      onClose();
      setNotes('');
      setSelectedSellerId('');
    } catch (error) {
      console.error('Error activating membership:', error);
      toast({
        title: 'Error',
        description: 'No se pudo activar la membresía. Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-slate-900 border-white/20 text-white max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-400" />
            Activar Membresía
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto flex-1 pr-2">
          {/* Member info */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3">
            <h3 className="text-sm font-medium text-white/50 mb-3">Información del Cliente</h3>
            
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-blue-400" />
              <span className="text-white font-medium">{member.profile.nombre}</span>
            </div>
            
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-green-400" />
              <span className="text-white/70">{member.profile.telefono}</span>
            </div>
            
            <div className="flex items-center gap-3">
              <Smartphone className="w-4 h-4 text-purple-400" />
              <span className="text-white/70">{member.profile.modelo_celular}</span>
            </div>
          </div>

          {/* Seller Selection - NEW */}
          <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/5 rounded-xl p-4 border border-amber-500/30">
            <div className="flex items-center gap-2 mb-3">
              <UserCheck className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-medium text-amber-300">¿Quién realizó la venta?</h3>
            </div>
            
            <Select 
              value={selectedSellerId} 
              onValueChange={setSelectedSellerId}
              disabled={loadingSellers}
            >
              <SelectTrigger className="bg-black/30 border-amber-500/30 text-white">
                <SelectValue placeholder={loadingSellers ? "Cargando vendedores..." : "Seleccionar vendedor..."} />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/20">
                {!loadingSellers && sellers.length === 0 ? (
                  <SelectItem value="__empty__" disabled className="text-white/50">
                    No hay vendedores disponibles
                  </SelectItem>
                ) : (
                  sellers.map(seller => (
                    <SelectItem 
                      key={seller.user_id} 
                      value={seller.user_id}
                      className="text-white focus:bg-amber-500/20 focus:text-white"
                    >
                      <div className="flex items-center gap-2">
                        <span>{seller.nombre}</span>
                        {seller.ubicacion_tienda && (
                          <span className="text-white/40 text-xs">({seller.ubicacion_tienda})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            
            {selectedSellerId && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className="text-amber-400 font-medium">+{planPoints[plan]} pts</span>
                <span className="text-white/50">se asignarán por venta {planLabels[plan]}</span>
              </div>
            )}
          </div>

          {/* Plan details */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-sm font-medium text-white/50 mb-3">Detalles del Plan</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-white/50 text-xs">Plan</p>
                <p className={`font-bold text-lg ${planColors[plan]}`}>
                  {planLabels[plan]}
                </p>
              </div>
              <div>
                <p className="text-white/50 text-xs">Período</p>
                <p className="text-white font-semibold">{periodLabels[billing_period]}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <DollarSign className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-white/50 text-xs">Monto a registrar</p>
                <p className="text-green-400 font-bold text-2xl">
                  ₡{amount.toLocaleString('es-CR')}
                </p>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-medium text-white/50">Fechas de la Membresía</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-white/50 text-xs">Fecha de inicio</p>
                <p className="text-white font-medium">Hoy</p>
              </div>
              <div>
                <p className="text-white/50 text-xs">Fecha de vencimiento</p>
                <p className="text-white font-medium">
                  {new Date(calculateEndDate()).toLocaleDateString('es-CR', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-white/70 text-sm">Notas del pago (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej: Pago en efectivo, transferencia SINPE, etc."
              className="mt-2 bg-white/5 border-white/20 text-white placeholder:text-white/30 resize-none"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 flex-shrink-0 pt-4 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={onClose}
            className="border border-white/20 text-white hover:bg-white/10 bg-transparent"
            disabled={submitting}
          >
            Volver
          </Button>
          <Button
            onClick={handleActivate}
            disabled={submitting || !selectedSellerId}
            className="bg-green-600 hover:bg-green-700 text-white gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Activar y Registrar Pago
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ActivationDialog;
